/**
 * klubb-plp-filter — filters and sorting update the product list in place.
 * Ticking a box, changing the sort or removing a chip fetches the filtered
 * page, swaps the results and the filter panel, and pushes the URL, so the
 * page never reloads or jumps to the top. Without JS the forms still submit
 * as plain GET. ESM module; exports pure helpers for unit tests.
 */

/** The URL a filter form would submit to, with empty fields and paging dropped. */
export function formUrl(form, base) {
  const url = new URL(form.getAttribute('action') || base, base);
  url.search = '';
  const data = new FormData(form);
  for (const [name, value] of data.entries()) {
    if (name === 'page' || String(value).trim() === '') continue;
    url.searchParams.append(name, value);
  }
  return url.toString();
}

/** Which filter groups and category branches are open, by their label text. */
export function openState(panel) {
  const groups = [];
  panel.querySelectorAll('details.klubb-filter-group').forEach((d) => {
    const title = d.querySelector('.klubb-filter-group__title');
    if (title) groups.push({ label: title.textContent.trim(), open: d.open });
  });
  const branches = [];
  panel.querySelectorAll('[data-klubb-expand]').forEach((btn) => {
    branches.push({ value: btn.getAttribute('data-klubb-expand'), open: btn.getAttribute('aria-expanded') === 'true' });
  });
  return { groups, branches };
}

export function restoreOpenState(panel, state) {
  if (!state) return;
  panel.querySelectorAll('details.klubb-filter-group').forEach((d) => {
    const title = d.querySelector('.klubb-filter-group__title');
    const saved = title && state.groups.find((g) => g.label === title.textContent.trim());
    if (saved) d.open = saved.open;
  });
  state.branches.forEach((b) => {
    const btn = panel.querySelector(`[data-klubb-expand="${cssEscape(b.value)}"]`);
    if (btn && b.open) setExpanded(btn, true);
  });
}

export function setExpanded(btn, open) {
  btn.setAttribute('aria-expanded', open ? 'true' : 'false');
  const list = btn.ownerDocument.getElementById(btn.getAttribute('aria-controls'));
  if (list) list.hidden = !open;
}

/**
 * Replaces the results and the filter form contents with those of the
 * fetched document. Returns false when the response is not a PLP page.
 */
export function swapPage(doc, nextDoc) {
  const main = doc.querySelector('[data-klubb-plp-main]');
  const nextMain = nextDoc.querySelector('[data-klubb-plp-main]');
  const form = doc.querySelector('[data-klubb-filters] form');
  const nextForm = nextDoc.querySelector('[data-klubb-filters] form');
  if (!main || !nextMain || !form || !nextForm) return false;

  const state = openState(form);
  main.replaceChildren(...Array.from(nextMain.childNodes).map((n) => doc.importNode(n, true)));
  form.replaceChildren(...Array.from(nextForm.childNodes).map((n) => doc.importNode(n, true)));
  restoreOpenState(form, state);
  return true;
}

function cssEscape(value) {
  return typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(value) : String(value).replace(/"/g, '\\"');
}

(function () {
  'use strict';
  if (typeof document === 'undefined') return;

  let controller = null;

  function busy(on) {
    document.querySelectorAll('[data-klubb-plp-main], [data-klubb-filters] form').forEach((el) => {
      if (on) el.setAttribute('aria-busy', 'true');
      else el.removeAttribute('aria-busy');
    });
  }

  function focusKey(el) {
    if (!el || !el.name) return null;
    return { name: el.name, value: el.value, tag: el.tagName };
  }

  function refocus(key) {
    if (!key) return;
    const form = document.querySelector('[data-klubb-filters] form');
    if (!form) return;
    const el = key.tag === 'SELECT'
      ? document.querySelector('.klubb-plp-toolbar__sort select')
      : Array.from(form.elements).find((e) => e.name === key.name && e.value === key.value);
    if (el) el.focus({ preventScroll: true });
  }

  function load(url, { push = true, focus = null } = {}) {
    if (typeof fetch !== 'function') { window.location.href = url; return; }
    if (controller) controller.abort();
    controller = new AbortController();
    busy(true);
    fetch(url, { credentials: 'same-origin', signal: controller.signal })
      .then((res) => {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then((html) => {
        const nextDoc = new DOMParser().parseFromString(html, 'text/html');
        if (!swapPage(document, nextDoc)) throw new Error('not a product list');
        if (push) history.pushState({ klubbPlp: true }, '', url);
        busy(false);
        refocus(focus);
      })
      .catch((err) => {
        if (err && err.name === 'AbortError') return;
        window.location.href = url;
      });
  }

  function wire() {
    document.addEventListener('change', (event) => {
      const form = event.target.closest && event.target.closest('[data-klubb-filters] form, .klubb-plp-toolbar__sort');
      if (!form) return;
      if (event.target.type === 'number') return; // price: applied with the button
      load(formUrl(form, window.location.href), { focus: focusKey(event.target) });
    });

    document.addEventListener('submit', (event) => {
      const form = event.target.closest && event.target.closest('[data-klubb-filters] form, .klubb-plp-toolbar__sort');
      if (!form) return;
      event.preventDefault();
      load(formUrl(form, window.location.href));
    });

    document.addEventListener('click', (event) => {
      const expand = event.target.closest && event.target.closest('[data-klubb-expand]');
      if (expand) {
        event.preventDefault();
        setExpanded(expand, expand.getAttribute('aria-expanded') !== 'true');
        return;
      }
      const link = event.target.closest && event.target.closest('.klubb-plp-filters__reset, .klubb-plp-toolbar__chip');
      if (!link || !link.getAttribute('href')) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button > 0) return;
      event.preventDefault();
      load(new URL(link.getAttribute('href'), window.location.href).toString());
    });

    window.addEventListener('popstate', () => {
      if (document.querySelector('[data-klubb-plp-main]')) load(window.location.href, { push: false });
    });
    history.replaceState({ klubbPlp: true }, '', window.location.href);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wire);
  } else {
    wire();
  }
})();
